import Icon from 'common/Icon'
import { RED } from 'constants/colors'
import { useState, useEffect, memo, useRef } from 'react'

type ErrorComponentProps = {
  error: AlertMessage
  disappearInterval?: number
  className?: string
  disappearCallback?: () => void
  dismissible?: boolean
  showIcon?: boolean
}

// Success component and Error component looks similar
// but keeping them seperately for future changes
const ErrorComponent: React.FC<ErrorComponentProps> = ({
  error,
  className = '',
  disappearInterval = 3000,
  disappearCallback,
  dismissible = true,
  showIcon = false,
}) => {
  const showAlertTimeout = useRef(null)
  const [lastUpdated, setLastUpdated] = useState<number>(error?.timestamp ?? 0)
  const [visibleState, setVisibleState] = useState<boolean>(!!error?.timestamp)
  useEffect(() => {
    if (error?.timestamp && lastUpdated !== error?.timestamp) {
      setVisibleState(true)
      setLastUpdated(error.timestamp)

      if (disappearInterval > 0) {
        if (showAlertTimeout?.current) {
          clearTimeout(showAlertTimeout.current)
        }

        showAlertTimeout.current = setTimeout(() => {
          setVisibleState(false)
          /* The disapper callback is taken to set the error to null as in case of modals, the cross icon in modal
          is pushed down if there's an error. setVisible to false doesn't set the error in parent component to null,
          so the cross icon is still pushed down.
          TODO Add logic that in case of setVisible to false, the error in parent component is also set to null.*/
          !!disappearCallback && disappearCallback()
          showAlertTimeout.current = null
        }, disappearInterval)
      }
    } else {
      setVisibleState(false)
    }

    return () => {
      if (showAlertTimeout?.current) {
        clearTimeout(showAlertTimeout.current)
      }
    }
  }, [error?.timestamp])

  if (visibleState) {
    const handleClose = () => {
      if (showAlertTimeout.current) {
        clearTimeout(showAlertTimeout.current)
        showAlertTimeout.current = null
      }

      setVisibleState(false)
      !!disappearCallback && disappearCallback()
    }

    return (
      <div
        className={`~bg-[#f8d7da] ~border ~border-[#f5c2c7] ~text-[#842029] ~rounded ~p-3 ~flex ~items-start ~justify-between ${className}`}
        role="alert"
      >
        <div className="~flex ~items-center ~gap-3">
          {showIcon && <Icon icon="alert" fill={RED} className="fa-lg" />}
          <span>{error?.message ?? 'Oops, something went wrong!'}</span>
        </div>
        {dismissible && (
          <button
            type="button"
            onClick={handleClose}
            className="~ml-3 ~bg-transparent ~border-0 ~cursor-pointer ~text-[#842029] ~text-xl ~leading-none ~p-0"
            aria-label="Close"
          >
            ×
          </button>
        )}
      </div>
    )
  }

  return null
}

const successPropsComparison = (
  previousProps: ErrorComponentProps,
  nextProps: ErrorComponentProps
) => {
  return previousProps?.error?.timestamp === nextProps?.error?.timestamp
}

export default memo(ErrorComponent, successPropsComparison)
